import type { IMappedGeoMappingItem } from 'partner360/components/filters'

import type { I621Form } from '../../../api/types'
import type { IPartnerDocumentSection } from '../../../lib/documentsAndLinks'
import { buildGeoMappingTree } from '../../../lib/revenueAndSobHierarchy/geoMappingHierarchy'
import { normalizeGeoKey } from '../../../lib/revenueAndSobHierarchy/normalizeGeoKey'
import type { IDocumentsNavigatorNode, TDocumentsGrouping } from '../types'

import { countDocuments } from './countDocuments'

interface IParams {
  sections: IPartnerDocumentSection[]
  partners: I621Form[]
  geoMapping: IMappedGeoMappingItem[]
  grouping: TDocumentsGrouping
}
export const buildDocumentsNavigationTree = ({
  sections,
  partners,
  geoMapping,
  grouping,
}: IParams): IDocumentsNavigatorNode[] => {
  const inScope = sections.filter((section) => countDocuments([section]) > 0)
  if (grouping === 'partner') {
    return inScope.map((section) => ({
      key: section.partnerId,
      label: section.partnerName,
      description: partners.find((partner) => partner.PartnerId.trim() === section.partnerId)
        ?.Country,
      sections: [section],
      children: [],
    }))
  }
  const roots: IDocumentsNavigatorNode[] = []
  const byKey = new Map<string, IDocumentsNavigatorNode>()
  const add = (
    key: string,
    label: string,
    section: IPartnerDocumentSection,
    siblings: IDocumentsNavigatorNode[]
  ) => {
    const node: IDocumentsNavigatorNode = byKey.get(key) ?? {
      key,
      label,
      sections: [],
      children: [],
    }
    if (!byKey.has(key)) {
      byKey.set(key, node)
      siblings.push(node)
    }
    node.sections.push(section)
    return node
  }
  if (grouping === 'creator') {
    inScope.forEach((section) => {
      const creators = new Map<string, string>()
      ;[...section.eltDocuments, ...section.qbrDocuments].forEach((document) => {
        const name = document.CreatedBy?.trim() || 'Unknown creator'
        creators.set(name.toLowerCase(), name)
      })
      creators.forEach((label, key) => {
        const matches = (document: { CreatedBy?: string }) =>
          (document.CreatedBy?.trim() || 'Unknown creator').toLowerCase() === key
        add(
          key,
          label,
          {
            ...section,
            eltDocuments: section.eltDocuments.filter(matches),
            qbrDocuments: section.qbrDocuments.filter(matches),
          },
          roots
        )
      })
    })
    return roots.sort((left, right) => left.label.localeCompare(right.label))
  }
  const { chainByCountry } = buildGeoMappingTree(geoMapping)
  const partnerById = new Map(partners.map((partner) => [partner.PartnerId.trim(), partner]))
  inScope.forEach((section) => {
    const partner = partnerById.get(section.partnerId)
    const country = normalizeGeoKey(partner?.CountryCode ?? '')
    const chain = chainByCountry.get(country) ?? []
    if (chain.length === 0) {
      add(`unmapped:${country}`, partner?.Country || 'Unmapped', section, roots)
      return
    }
    chain.reduce(
      ({ siblings, key }, item) => {
        const nextKey = `${key}/${item.itemId}`
        return { key: nextKey, siblings: add(nextKey, item.name, section, siblings).children }
      },
      { siblings: roots, key: '' }
    )
  })
  return roots
}

export const findDocumentsNavigatorNode = (
  nodes: IDocumentsNavigatorNode[],
  key: string
): IDocumentsNavigatorNode | undefined => {
  for (const node of nodes) {
    if (node.key === key) {
      return node
    }
    const child = findDocumentsNavigatorNode(node.children, key)
    if (child) {
      return child
    }
  }
  return undefined
}
